import React from 'react';
import { Helmet } from 'react-helmet';

function SEO() {
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>Sayem | Mohammad Sayem Chowdhury | Research Assistant</title>
      <meta
        name="description"
        content="Mohammad Sayem Chowdhury - Computer science researcher focused on interpretable deep learning, medical imaging, and safety-critical vision. Publications, research, and academic background."
      />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="profile" />
      <meta property="og:url" content="https://saayeeem.github.io/website/" />
      <meta property="og:site_name" content="Sayem Portfolio" />
      <meta property="og:title" content="Sayem | Mohammad Sayem Chowdhury | Research Assistant" />
      <meta
        property="og:description"
        content="Mohammad Sayem Chowdhury - Computer science researcher focused on interpretable deep learning, medical imaging, and safety-critical vision. Publications, research, and academic background."
      />
      <meta property="og:image" content="https://saayeeem.github.io/website/assets/images/profile.webp" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:site" content="@saayeeem" />
      <meta property="twitter:creator" content="@saayeeem" />
      <meta property="twitter:url" content="https://saayeeem.github.io/website/" />
      <meta property="twitter:title" content="Sayem | Mohammad Sayem Chowdhury | Research Assistant" />
      <meta
        property="twitter:description"
        content="Mohammad Sayem Chowdhury - Computer science researcher focused on interpretable deep learning, medical imaging, and safety-critical vision. Publications, research, and academic background."
      />
      <meta property="twitter:image" content="https://saayeeem.github.io/website/assets/images/profile.webp" />

      {/* Additional Meta Tags */}
      <meta
        name="keywords"
        content="Sayem, Mohammad Sayem Chowdhury, AI, Research, Portfolio, Data Science, Developer, sayem portfolio, sayem ai, sayem research, sayem website"
      />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="author" content="Mohammad Sayem Chowdhury" />
      <meta name="subject" content="Personal Portfolio of Mohammad Sayem Chowdhury" />
      <meta name="coverage" content="Worldwide" />
      <meta name="distribution" content="Global" />
      <meta name="rating" content="General" />
      <meta name="url" content="https://saayeeem.github.io/website/" />
      <meta name="identifier-URL" content="https://saayeeem.github.io/website/" />
      <meta name="directory" content="submission" />
      <meta name="category" content="Technology" />
      <meta name="target" content="all" />
      <meta name="audience" content="all" />
      <link rel="canonical" href="https://saayeeem.github.io/website/" />

      {/* Alternate hreflang links */}
      <link rel="alternate" href="https://saayeeem.github.io/website/" hrefLang="en" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {`
{
  "@context": "https://schema.org",
  "@type": "Person",
  "name": "Mohammad Sayem Chowdhury",
  "alternateName": "Sayem",
  "url": "https://saayeeem.github.io/website/",
  "image": "https://saayeeem.github.io/website/assets/images/profile.webp",
  "sameAs": [
    "https://www.linkedin.com/in/mohammad-sayem-chowdhury/",
    "https://github.com/saayeeem",
    "https://scholar.google.com/citations?hl=en&authuser=1&user=wDPGD9MAAAAJ",
    "https://www.researchgate.net/profile/Mohammad_Sayem_Chowdhury",
    "https://www.scopus.com/authid/detail.uri?authorId=58961274500",
    "https://twitter.com/saayeeem"
  ],
  "jobTitle": "Research Assistant",
  "alumniOf": {
    "@type": "CollegeOrUniversity",
    "name": "American International University-Bangladesh"
  },
  "award": [
    "Magna Cum Laude (Silver Medal)",
    "Google Data Analytics Certificate",
    "IBM Data Analytics Professional Certificate"
  ],
  "description": "Mohammad Sayem Chowdhury is a Research Assistant with an M.Sc. in Computer Science (Intelligent Systems). His research focuses on interpretable deep learning for medical imaging and safety-critical vision. Author of multiple peer-reviewed publications in IEEE, Springer, and Wiley venues. Recognized with Magna Cum Laude in February 2025.",
  "knowsAbout": [
    "Research",
    "Machine Learning",
    "Deep Learning",
    "Natural Language Processing",
    "Data Analysis",
    "Web Development",
    "Computer Vision",
    "Human-Centered AI",
    "Python",
    "PHP",
    "Tableau",
    "R",
    "JavaScript",
    "React",
    "Node.js",
    "Data Science"
  ],
  "knowsLanguage": ["English", "Bengali"],
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Chattogram",
    "addressCountry": "Bangladesh"
  },
  "email": "m.sayem.c@gmail.com",
  "memberOf": {
    "@type": "Organization",
    "name": "IEEE"
  },
  "publication": [
    {
      "@type": "PublicationEvent",
      "name": "Leveraging Deep Neural Networks to Uncover Unprecedented Levels of Precision in the Diagnosis of Hair and Scalp Disorders",
      "url": "https://doi.org/10.1111/srt.13660",
      "isAccessibleForFree": true,
      "publisher": "Wiley"
    },
    {
      "@type": "PublicationEvent",
      "name": "SafeguardNet: Enhancing Corporate Safety via Tailored Deep Transfer Learning for Threat Recognition",
      "url": "https://ieeexplore.ieee.org/document/10637407",
      "isAccessibleForFree": true,
      "publisher": "IEEE Access"
    },
    {
      "@type": "PublicationEvent",
      "name": "Deep Learning-Based Multistage Fire Detection System and Emerging Direction",
      "url": "https://doi.org/10.3390/fire7120451",
      "isAccessibleForFree": true,
      "publisher": "MDPI (FIRE)"
    },
    {
      "@type": "PublicationEvent",
      "name": "LeafDNet: Transforming Leaf Disease Diagnosis Through Deep Transfer Learning",
      "url": "https://doi.org/10.1002/pld3.70047",
      "isAccessibleForFree": true,
      "publisher": "Wiley"
    },
    {
      "@type": "PublicationEvent",
      "name": "Unveiling the Unique Dermatological Signatures of Human Pox Diseases through Deep Transfer Learning Model Based on DenseNet and Validation with Explainable AI",
      "url": "https://doi.org/10.1007/978-981-97-3966-0_7",
      "isAccessibleForFree": true,
      "publisher": "Springer Nature"
    },
    {
      "@type": "PublicationEvent",
      "name": "Optimized Approaches for Real-Life Sentiment Analysis in E-Commerce",
      "url": "https://link.springer.com/book/9789819632152",
      "isAccessibleForFree": false,
      "publisher": "Springer (Accepted Chapter)"
    }
  ],
  "workLocation": {
    "@type": "Place",
    "name": "Dhaka, Bangladesh"
  },
  "birthPlace": {
    "@type": "Place",
    "name": "Chattogram, Bangladesh"
  },
  "nationality": "Bangladeshi",
  "gender": "Male",
  "hasOccupation": [
    {
      "@type": "Occupation",
      "name": "Research Assistant",
      "description": "Conducting research in interpretable deep learning, medical imaging, and safety-critical vision."
    },
    {
      "@type": "Occupation",
      "name": "Computer Science Researcher",
      "description": "Research in machine learning, deep learning, and natural language processing."
    },
    {
      "@type": "Occupation",
      "name": "Data Analyst",
      "description": "Specializing in data analysis using statistical and analytical tools."
    },
    {
      "@type": "Occupation",
      "name": "Web Developer",
      "description": "Developing modern web applications using current frameworks and technologies."
    }
  ],
  "award": [
    {
      "@type": "Award",
      "name": "Magna Cum Laude (Silver Medal)",
      "awardDate": "2025-02-22",
      "description": "Awarded for academic excellence."
    }
  ]
}
        `}
      </script>
      <script type="application/ld+json">
        {`
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Who is Sayem?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Mohammad Sayem Chowdhury is a computer science researcher with an M.Sc. in Intelligent Systems. His research focuses on interpretable deep learning for medical imaging and safety-critical vision applications."
              }
            },
            {
              "@type": "Question",
              "name": "How can I contact Sayem?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "You can contact Sayem via the contact form on this website or by email at m.sayem.c@gmail.com."
              }
            },
            {
              "@type": "Question",
              "name": "Where can I find Sayem's publications?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "All of Sayem's publications are listed in the Publications section of this website, with links to the original papers."
              }
            },
            {
              "@type": "Question",
              "name": "Can I collaborate with Sayem?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes! Sayem is open to research and professional collaborations. Please use the contact form to reach out."
              }
            }
          ]
        }
        `}
      </script>
      <script type="application/ld+json">
        {`
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://saayeeem.github.io/website/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "About Sayem",
              "item": "https://saayeeem.github.io/website/#about"
            }
          ]
        }
        `}
      </script>
    </Helmet>
  );
}

export default SEO;
